import React, { useState } from 'react';
import { 
  FaTimesCircle,
  FaMoneyBillWave,
  FaCalendarTimes,
  FaExclamationTriangle,
  FaClock,
  FaCheckCircle,
  FaPhone,
  FaWhatsapp,
  FaEnvelope,
  FaArrowRight,
  FaFileInvoiceDollar,
  FaPercentage,
  FaCalendarAlt,
  FaCar,
  FaHotel,
  FaPlane,
  FaShip,
  FaInfoCircle,
  FaUserClock
} from 'react-icons/fa';
import '../style.css';

const CancellationPolicy = () => {
  const [cancelActiveSection, setCancelActiveSection] = useState('general');

  const cancelSections = [
    {
      id: 'general',
      title: 'General Policy',
      icon: <FaTimesCircle />,
      color: '#e74c3c'
    },
    {
      id: 'charges',
      title: 'Cancellation Charges',
      icon: <FaPercentage />,
      color: '#8B4513'
    },
    {
      id: 'refund',
      title: 'Refund Process',
      icon: <FaMoneyBillWave />,
      color: '#2ecc71'
    },
    {
      id: 'modification',
      title: 'Modification',
      icon: <FaCalendarAlt />,
      color: '#3498db'
    },
    {
      id: 'special',
      title: 'Special Cases',
      icon: <FaExclamationTriangle />,
      color: '#f39c12'
    },
    {
      id: 'timeline',
      title: 'Timeline',
      icon: <FaUserClock />,
      color: '#9b59b6'
    }
  ];

  const cancelGeneralPolicy = [
    {
      title: 'Policy Overview',
      content: `This cancellation policy applies to all services booked through Shree Balaji Travels including taxi services, tour packages, hotel bookings, cruise bookings, and pilgrimage services. By making a booking, you agree to abide by this cancellation policy.`
    },
    {
      title: 'Cancellation Window',
      content: `• Cancellations must be made via phone, WhatsApp, or email
      • Cancellation is effective only after receiving confirmation from us
      • Time of cancellation is based on Indian Standard Time (IST)
      • Weekend and holiday cancellations follow the same policy`
    },
    {
      title: 'No-Show Policy',
      content: `• Failure to appear at scheduled pickup: 100% cancellation charge
      • 30-minute grace period at pickup location
      • After grace period: ₹100 per 30 minutes waiting charge
      • No-show for tours/packages: Full amount forfeited`
    },
    {
      title: 'Policy Changes',
      content: `• We reserve the right to modify this policy
      • Changes effective immediately upon website update
      • Existing bookings follow policy at time of booking
      • Major changes communicated to customers`
    }
  ];

  const cancelCharges = [
    {
      title: 'Taxi Services',
      content: `• Local/Outstation (12+ hours notice): 90% refund
      • Local/Outstation (6-12 hours notice): 70% refund
      • Local/Outstation (3-6 hours notice): 50% refund
      • Local/Outstation (<3 hours notice): No refund
      • Airport/Railway pickup (<4 hours notice): No refund`
    },
    {
      title: 'Tour Packages',
      content: `• 15+ days before departure: 95% refund
      • 7-14 days before departure: 80% refund
      • 3-6 days before departure: 60% refund
      • 1-2 days before departure: 30% refund
      • Day of departure: No refund
      • Non-refundable component: 5% processing fee`
    },
    {
      title: 'Hotel Bookings',
      content: `• 7+ days before check-in: Full refund
      • 3-6 days before check-in: 70% refund
      • 1-2 days before check-in: 50% refund
      • Same day cancellation: 25% refund
      • No-show: No refund
      • Peak season: Special cancellation terms apply`
    },
    {
      title: 'Pind Daan Services',
      content: `• 7+ days before service: 95% refund
      • 3-6 days before service: 80% refund
      • 1-2 days before service: 50% refund
      • Same day cancellation: 25% refund
      • Materials purchased: Non-refundable`
    }
  ];

  const cancelRefundProcess = [
    {
      title: 'Refund Methods',
      content: `• Refunds processed to original payment method
      • UPI payments: Refund within 24 hours
      • Credit/Debit cards: 3-7 working days
      • Bank transfers: 2-5 working days
      • Cash payments: Bank transfer or cheque`
    },
    {
      title: 'Processing Time',
      content: `• Standard processing: 3-7 working days
      • Peak season: 7-14 working days
      • Refund status: Track via booking ID
      • Delayed refunds: Contact customer care
      • GST component: Non-refundable`
    },
    {
      title: 'Refund Documentation',
      content: `• Booking ID required
      • Cancellation confirmation number
      • Bank details for cash refunds
      • ID proof for verification
      • Cancellation reason (optional)`
    },
    {
      title: 'Deductions',
      content: `• Processing fee: ₹100 or 5% (whichever is higher)
      • GST: Non-refundable (5%)
      • Bank charges: As applicable
      • Non-refundable components: Clearly mentioned at booking
      • Service tax: As per government norms`
    }
  ];

  const cancelModification = [
    {
      title: 'Date/Time Changes',
      content: `• 6+ hours before: Free modification
      • 3-6 hours before: ₹100 modification fee
      • <3 hours before: Treated as cancellation
      • Multiple modifications: Additional charges may apply
      • Subject to availability`
    },
    {
      title: 'Service Changes',
      content: `• Vehicle upgrade: Pay difference immediately
      • Vehicle downgrade: No refund for difference
      • Package modification: As per new package terms
      • Route changes: Subject to driver approval
      • Additional stops: ₹50 per stop`
    },
    {
      title: 'Name Changes',
      content: `• Primary passenger: ₹200 fee
      • Secondary passengers: ₹100 per person
      • Corporate bookings: As per agreement
      • Identification documents required
      • 24 hours notice required`
    },
    {
      title: 'Group Bookings',
      content: `• 10+ passengers: Special terms apply
      • Partial cancellation: Pro-rata charges
      • Group leader changes: ₹500 fee
      • Advance notice: 48 hours minimum
      • Written confirmation required`
    }
  ];

  const cancelSpecialCases = [
    {
      title: 'Natural Calamities',
      content: `• Floods, earthquakes, storms: Full refund
      • Government travel restrictions: Full refund
      • Road closures: Alternative or full refund
      • Medical emergency: 90% refund (with proof)
      • Documentation required for special cases`
    },
    {
      title: 'Medical Emergencies',
      content: `• Hospital admission proof required
      • Doctor's certificate mandatory
      • Immediate family only
      • 90% refund for genuine cases
      • Process within 30 days`
    },
    {
      title: 'Vehicle Breakdown',
      content: `• Our vehicle breakdown: Full refund or alternative
      • Alternative not acceptable: Full refund
      • Compensation for inconvenience
      • Immediate assistance provided
      • No cancellation charges`
    },
    {
      title: 'Force Majeure',
      content: `• Political unrest, strikes
      • Natural disasters
      • Pandemic situations
      • Government orders
      • Full refund in such cases`
    }
  ];

  const cancelTimeline = [
    {
      title: 'Immediate (0-3 hours)',
      content: `• No refund applicable
      • Emergency contact: +91 9415799879
      • Last-minute alternatives may be possible
      • Subject to availability
      • Additional charges may apply`
    },
    {
      title: 'Short Notice (3-6 hours)',
      content: `• 50% refund for taxi services
      • 25% refund for packages
      • ₹100 processing fee
      • Contact via WhatsApp for quick response
      • Confirmation within 30 minutes`
    },
    {
      title: 'Medium Notice (6-24 hours)',
      content: `• 70-90% refund depending on service
      • Processing time: 3-5 days
      • Email confirmation sent
      • Refund tracking available
      • Customer care support`
    },
    {
      title: 'Advance Notice (24+ hours)',
      content: `• 90-100% refund
      • Processing: 2-3 days
      • No queries generally
      • Smooth refund process
      • Customer satisfaction priority`
    }
  ];

  const getCancelActiveContent = () => {
    switch(cancelActiveSection) {
      case 'general': return cancelGeneralPolicy;
      case 'charges': return cancelCharges;
      case 'refund': return cancelRefundProcess;
      case 'modification': return cancelModification;
      case 'special': return cancelSpecialCases;
      case 'timeline': return cancelTimeline;
      default: return cancelGeneralPolicy;
    }
  };

  const getCancelSectionTitle = () => {
    return cancelSections.find(s => s.id === cancelActiveSection)?.title || 'Cancellation Policy';
  };

  const calculateRefund = (amount, hours) => {
    if (hours >= 24) return Math.round(amount * 0.95);
    if (hours >= 12) return Math.round(amount * 0.7);
    if (hours >= 6) return Math.round(amount * 0.5);
    if (hours >= 3) return Math.round(amount * 0.3);
    return 0;
  };

  return (
    <div className="cancel-wrapper">
      {/* Header */}
      <div className="cancel-hero-section">
        <div className="cancel-hero-content">
          <h1 className="cancel-main-title">Cancellation Policy</h1>
          <p className="cancel-subtitle">Shree Balaji Travels - Varanasi</p>
          <div className="cancel-badge-container">
            <span className="cancel-badge-item">
              <FaClock /> Clear Timelines
            </span>
            <span className="cancel-badge-item">
              <FaCheckCircle /> Fair Refunds
            </span>
            <span className="cancel-badge-item">
              <FaInfoCircle /> Transparent Process
            </span>
          </div>
        </div>
      </div>

      <div className="cancel-content-wrapper">
        {/* Side Navigation */}
        <div className="cancel-side-nav">
          <div className="cancel-nav-panel">
            <h3 className="cancel-nav-title">Policy Sections</h3>
            {cancelSections.map((section) => (
              <button
                key={section.id}
                className={`cancel-nav-button ${cancelActiveSection === section.id ? 'cancel-nav-active' : ''}`}
                onClick={() => setCancelActiveSection(section.id)}
                style={{ '--cancel-btn-color': section.color }}
              >
                <span className="cancel-button-icon" style={{ color: section.color }}>
                  {section.icon}
                </span>
                {section.title}
                {cancelActiveSection === section.id && <FaArrowRight className="cancel-nav-arrow" />}
              </button>
            ))}
          </div>

          {/* Quick Calculator */}
          <div className="cancel-calculator">
            <h4 className="cancel-calc-title">Refund Estimator</h4>
            <div className="cancel-calc-content">
              <p className="cancel-calc-text">Enter booking amount and notice period</p>
              <div className="cancel-calc-inputs">
                <div className="cancel-calc-input-group">
                  <label>Amount (₹)</label>
                  <input type="number" placeholder="5000" className="cancel-calc-input" />
                </div>
                <div className="cancel-calc-input-group">
                  <label>Notice (hours)</label>
                  <input type="number" placeholder="24" className="cancel-calc-input" />
                </div>
              </div>
              <button className="cancel-calc-button">
                <FaFileInvoiceDollar /> Calculate Refund
              </button>
              <div className="cancel-calc-result">
                Estimated Refund: <span className="cancel-calc-amount">₹4,750</span>
              </div>
            </div>
          </div>

          {/* Contact Box */}
          <div className="cancel-contact-panel">
            <h4 className="cancel-contact-title">Need Help?</h4>
            <p className="cancel-contact-text">Contact cancellation desk</p>
            <div className="cancel-contact-methods">
              <a href="tel:+919415799879" className="cancel-contact-link">
                <FaPhone /> +91 9415799879
              </a>
              <a href="https://wa.me/919415799879" className="cancel-contact-link">
                <FaWhatsapp /> WhatsApp
              </a>
              <a href="mailto:cancellations@shreebalajitravels.com" className="cancel-contact-link">
                <FaEnvelope /> Cancel Email
              </a>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="cancel-main-content">
          <div className="cancel-content-header">
            <div className="cancel-title-wrapper">
              {cancelSections.find(s => s.id === cancelActiveSection)?.icon}
              <h2 className="cancel-content-title">{getCancelSectionTitle()}</h2>
            </div>
            <p className="cancel-content-subtitle">
              Fair and transparent cancellation process for all our services
            </p>
          </div>

          {/* Policy Cards */}
          <div className="cancel-cards-container">
            {getCancelActiveContent().map((item, index) => (
              <div key={index} className="cancel-card-item">
                <div className="cancel-card-header">
                  <h3 className="cancel-card-title">{item.title}</h3>
                </div>
                <div className="cancel-card-body">
                  {item.content.split('\n').map((line, i) => (
                    <p key={i} className="cancel-text-line">
                      {line.trim().startsWith('•') ? (
                        <>
                          <span className="cancel-bullet">•</span>
                          <span className="cancel-text-content">{line.substring(1).trim()}</span>
                        </>
                      ) : (
                        line
                      )}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Important Notice */}
          <div className="cancel-notice-box">
            <div className="cancel-notice-icon">
              <FaExclamationTriangle />
            </div>
            <div className="cancel-notice-content">
              <h4 className="cancel-notice-title">Important Notice</h4>
              <p className="cancel-notice-text">
                All cancellations must be confirmed by our team. Verbal cancellations are not accepted. 
                Refund processing begins only after receiving written/electronic cancellation confirmation. 
                Weekend and holiday cancellations follow the same policy.
              </p>
            </div>
          </div>

          {/* Service Coverage */}
          <div className="cancel-services-box">
            <h4 className="cancel-services-title">Services Covered</h4>
            <div className="cancel-service-icons">
              <div className="cancel-service-icon">
                <FaCar />
                <span>Taxi Services</span>
                <small>Local & Outstation</small>
              </div>
              <div className="cancel-service-icon">
                <FaHotel />
                <span>Hotel Booking</span>
                <small>All categories</small>
              </div>
              <div className="cancel-service-icon">
                <FaPlane />
                <span>Air Tickets</span>
                <small>As per airline policy</small>
              </div>
              <div className="cancel-service-icon">
                <FaShip />
                <span>Cruise Booking</span>
                <small>River Ganges</small>
              </div>
              <div className="cancel-service-icon">
                <FaCalendarTimes />
                <span>Tour Packages</span>
                <small>Religious & Cultural</small>
              </div>
            </div>
          </div>

          {/* Timeline Visualization */}
          <div className="cancel-timeline-box">
            <h4 className="cancel-timeline-title">Cancellation Timeline</h4>
            <div className="cancel-timeline">
              <div className="cancel-timeline-item">
                <div className="cancel-timeline-dot red"></div>
                <div className="cancel-timeline-content">
                  <h5>0-3 Hours</h5>
                  <p>No Refund</p>
                </div>
              </div>
              <div className="cancel-timeline-item">
                <div className="cancel-timeline-dot orange"></div>
                <div className="cancel-timeline-content">
                  <h5>3-6 Hours</h5>
                  <p>30% Refund</p>
                </div>
              </div>
              <div className="cancel-timeline-item">
                <div className="cancel-timeline-dot yellow"></div>
                <div className="cancel-timeline-content">
                  <h5>6-12 Hours</h5>
                  <p>50% Refund</p>
                </div>
              </div>
              <div className="cancel-timeline-item">
                <div className="cancel-timeline-dot light-green"></div>
                <div className="cancel-timeline-content">
                  <h5>12-24 Hours</h5>
                  <p>70% Refund</p>
                </div>
              </div>
              <div className="cancel-timeline-item">
                <div className="cancel-timeline-dot green"></div>
                <div className="cancel-timeline-content">
                  <h5>24+ Hours</h5>
                  <p>95% Refund</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="cancel-footer-section">
        <div className="cancel-footer-content">
          <div className="cancel-footer-logo">
            <FaTimesCircle />
            <span>Cancellation Policy</span>
          </div>
          <p className="cancel-footer-text">
            Last Updated: {new Date().toLocaleDateString('en-IN', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}
          </p>
          <div className="cancel-footer-notice">
            <FaClock />
            <p className="cancel-notice-footer-text">
              For immediate cancellations, call +91 9415799879. Refunds processed within 7 working days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;